export default {
  auth: {
    logout: 'Sign Out',
  },
  form: {
    create: 'Create',
    submit: 'Submit',
    submitting: 'Submitting...',
    continue: 'Continue',
    cancel: 'Cancel',
  },
  modules: {
    dashboard: {
      title: 'Dashboard',
      noPermissions: {
        title: 'No permissions yet',
        description:
          'Your admin account does not have any permissions assigned. Please contact an administrator to request access.',
      },
    },
    organizations: {
      title: 'Organization | Organizations',
      listing: 'Organization List',
      new: 'New Organization',
      edit: 'Edit Organization',
      delete: 'Delete Organization',
      name: 'Name',
      subdomain: 'Careers Subdomain',
      website: 'Website',
      websiteHint: 'The organization\'s website URL (e.g. "https://example.com").',
      enabled: 'Enabled',
      notes: 'Notes',
      selectModules: 'Select Modules',
      created: 'Created',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
      nameHint: 'This is the organization name in the system.',
      subdomainHint:
        'This is the subdomain where the organization\'s careers page will be hosted. (e.g. "acme.careers.atlasthq.com")',
      language: 'Language',
      selectLanguage: 'Select Language',
      languageHint: 'Select the default language for the organization.',
      dateFormat: 'Date Format',
      selectDateFormat: 'Select Date Format',
      dateFormatHint: 'Select the date format for the organization.',
      timeFormat: 'Time Format',
      selectTimeFormat: 'Select Time Format',
      timeFormat12h: '12-hour (AM/PM)',
      timeFormat24h: '24-hour',
      timeFormatHint: 'Select the time format for the organization.',
      enabledHint: 'This determines whether the organization is enabled or not.',
      deleteOrg: 'Delete Organization',
      deleteOrgWarning:
        'This operation is irreversible. It will delete the Organization and all permissions associated with this organization.',
      deleteConfirmationInput: 'Enter the name of the organization to delete ({deleteOrgName}):',
      deletedSuccessfully: 'Organization deleted successfully!',
      noOrganizations: 'No organizations found.',
      createFirst: 'Create your first organization to get started.',
      plan: 'Subscription Plan',
      selectPlan: 'Select a plan',
      planHint: 'Choose the subscription plan for this organization',
      customPricing: 'Custom pricing',
      error: {
        organizationNameRequired: 'Organization name is required',
        organizationHasDependencies: 'Organization has dependencies and cannot be deleted',
        organizationNameMinLength: 'Organization name must be at least {min} characters long',
        organizationNameMaxLength: 'Organization name must be at most {max} characters long',
        invalidLanguage: 'Unsupported language',
        invalidDateFormat: 'Unsupported date format',
        invalidTimeFormat: 'Unsupported time format',
        planRequired: 'Please select a plan',
        invalidWebsiteUrl: 'Please enter a valid URL',
      },
      impersonate: {
        button: 'Impersonate',
        dialogTitle: 'Impersonate Organization',
        dialogDescription: 'Select a user to open the portal as for {orgName}.',
        auditWarning: 'This action is logged in the audit trail for security purposes.',
        email: 'Email',
        name: 'Name',
        open: 'Open',
        opening: 'Opening...',
        cancel: 'Cancel',
        noUsers: 'This organization has no users to impersonate.',
        success: 'Portal opened as {email}',
        popupBlocked: 'Popup was blocked. Please allow popups for this site.',
        error: 'Failed to start impersonation session.',
        permissionDenied: 'You do not have permission to impersonate users.',
      },
      outreachOverride: {
        title: 'Outreach Override Recipient',
        help: 'When set, all LinkedIn outreach for this organization is redirected to this profile. Used for testing in production.',
        placeholder: 'e.g. john-doe-qa',
        save: 'Save',
        clear: 'Clear',
        notSet: 'Not set',
        saved: 'Outreach override updated',
        cleared: 'Outreach override cleared',
        error: 'Failed to update outreach override',
        confirmTitle: 'Redirect all LinkedIn outreach?',
        confirmDescription:
          'Every LinkedIn outreach message sent by this organization will be redirected to "{recipient}" instead of the intended prospect. Real candidates will not receive any messages until the override is cleared. Are you sure you want to continue?',
        confirmAction: 'Yes, redirect outreach',
      },
    },
    users: {
      title: 'User | Users',
      listing: 'User List',
      new: 'New User',
      edit: 'Edit User',
      newTitle: '@:modules.users.new for {organization}',
      name: 'Name',
      surname: 'Surname',
      email: 'Email',
      created: 'Created',
      orgId: 'Organization ID',
      selectGroups: 'Select Groups',
      enabled: 'Enabled',
      status: 'Status',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
      emailHint: 'This is the user email in the system.',
      deleteUser: 'Delete User',
      deleteUserWarning:
        'This operation is irreversible. It will delete the User and all permissions associated with this user.',
      deleteConfirmationInput: 'Enter the email of the user to delete ({deleteUserEmail}):',
      deletedSuccessfully: 'User deleted successfully!',
      noUsers: 'No users found.',
      createFirst: 'Create your first user to get started.',
      userStatus: {
        UNCONFIRMED: 'User has been created but not confirmed',
        CONFIRMED: 'User has been confirmed',
        EXTERNAL_PROVIDER: 'User signed in with a third-party IdP.',
        RESET_REQUIRED:
          'User is confirmed, but the user must request a code and reset their password before they can sign in.',
        FORCE_CHANGE_PASSWORD:
          'The user is confirmed and the user can sign in using a temporary password, but on first sign-in, the user must change their password to a new value before doing anything else.',
      },
      error: {
        emailRequired: 'Email is required',
        emailInvalid: 'Email is not valid',
      },
      permissions: {
        users_create: 'Create Users',
        users_read: 'View Users',
        users_update: 'Update Users',
        users_delete: 'Delete Users',
      },
    },
    candidates: {
      title: 'Candidates',
      permissions: {
        candidates_create: 'Create Candidates',
        candidates_read: 'View Candidates',
        candidates_update: 'Update Candidates',
        candidates_delete: 'Delete Candidates',
      },
    },
    ai_credits: {
      title: 'AI Credits',
      status: {
        title: 'Credit Status',
        balance: 'Total Balance',
        planBalance: 'Plan Balance',
        rolloverBalance: 'Rollover Balance',
        monthlyAllocation: 'Monthly Allocation',
        used: 'Used This Period',
        usage: 'Usage',
        usedLabel: 'used',
        remaining: 'remaining',
        unlimitedDescription: 'This organization has unlimited AI credits.',
      },
      ledger: {
        title: 'Transaction History',
        date: 'Date',
        type: 'Type',
        amount: 'Amount',
        balanceAfter: 'Balance',
        planBalanceAfter: 'Plan Balance',
        rolloverBalanceAfter: 'Rollover Balance',
        description: 'Description',
        noEntries: 'No credit transactions found.',
        showing: 'Showing {start} to {end} of {total} entries',
        historicalRowTooltip: 'Predates two-bucket model; use Balance only.',
      },
      transactionType: {
        ALLOCATION: 'Allocation',
        DEDUCTION: 'Deduction',
        TOP_UP: 'Top Up',
        PURCHASE: 'Purchase',
        ADJUSTMENT: 'Adjustment',
      },
      topUp: {
        title: 'Top Up',
        description:
          'Add credits to this organization. This is always additive to the current balance.',
        amount: 'Amount',
        amountPlaceholder: 'Enter credit amount (min 1)',
        amountMin: 'Amount must be at least 1',
        reason: 'Reason',
        reasonPlaceholder: 'e.g., Customer requested additional credits for Q1 hiring push',
        submit: 'Add Credits',
        success: 'Credits added successfully. New balance: {balance}',
      },
      adjust: {
        title: 'Adjust',
        description: 'Make a balance correction. Use negative values to remove credits.',
        amount: 'Amount',
        amountPlaceholder: 'Enter amount (positive or negative)',
        amountNotZero: 'Amount cannot be zero',
        reason: 'Reason',
        reasonPlaceholder: 'e.g., Correcting duplicate deduction from system error',
        negativeWarning: 'This will remove credits from the organization.',
        submit: 'Adjust Credits',
        success: 'Credits adjusted successfully. New balance: {balance}',
      },
    },
    llm_usage: {
      title: 'LLM Usage',
      search: {
        title: 'LLM Token Usage Search',
        filters: 'Search Filters',
        organizationId: 'Organization ID',
        userId: 'User ID',
        operation: 'Operation',
        model: 'Model',
        provider: 'Provider',
        dateRange: 'Date Range',
        pickDateRange: 'Pick a date range',
        searchButton: 'Search',
        searching: 'Searching...',
        reset: 'Reset',
        results: 'Results ({total} total)',
        timestamp: 'Timestamp',
        inputTokens: 'Input Tokens',
        outputTokens: 'Output Tokens',
        totalTokens: 'Total Tokens',
        totals: 'Totals:',
        showing: 'Showing {start} to {end} of {total} results',
        noResults: 'No results found. Try adjusting your search filters.',
        initialMessage: 'Enter search criteria and click "Search" to find LLM token usage logs.',
      },
    },
    feedback: {
      title: 'Feedback',
      tabs: {
        message: 'AI Message Feedback',
        general: 'General Feedback',
      },
      rating: {
        up: 'Thumbs Up',
        down: 'Thumbs Down',
      },
      status: {
        new: 'New',
        reviewed: 'Reviewed',
        resolved: 'Resolved',
      },
      category: {
        bug: 'Bug',
        idea: 'Idea',
        general: 'General',
      },
      search: {
        filters: 'Search Filters',
        organizationId: 'Organization ID',
        userId: 'User ID',
        jobId: 'Job ID',
        agent: 'Agent',
        rating: 'Rating',
        status: 'Status',
        category: 'Category',
        hasComment: 'Has Comment',
        commentSearch: 'Comment Search',
        commentPlaceholder: 'Search within comments...',
        messageSearch: 'Message Search',
        messagePlaceholder: 'Search within messages...',
        any: 'Any',
        dateRange: 'Date Range',
        pickDateRange: 'Pick a date range',
        searchButton: 'Search',
        searching: 'Searching...',
        reset: 'Reset',
        results: 'Results ({total} total)',
        timestamp: 'Timestamp',
        submitter: 'Submitter',
        organization: 'Organization',
        comment: 'Comment',
        showing: 'Showing {start} to {end} of {total} results',
        noResults: 'No results found. Try adjusting your search filters.',
        initialMessage: 'Enter search criteria and click "Search" to find feedback.',
      },
      general: {
        message: 'Message',
        status: 'Status',
        adminNotes: 'Admin Notes',
      },
      triage: {
        title: 'Triage Feedback',
        description: 'Update the status and add internal notes for this feedback.',
        notesPlaceholder: 'Internal notes (not visible to the user)...',
        saving: 'Saving...',
      },
      delete: {
        button: 'Delete feedback',
        title: 'Delete this feedback?',
        description: 'This permanently removes the feedback. This action cannot be undone.',
        confirm: 'Delete',
        success: 'Feedback deleted',
      },
      detail: {
        title: 'Message Feedback',
        clientContext: 'Client Context',
        session: 'Session Context',
        status: 'Status',
        ratedMessage: 'Rated message',
        messageNotLocated: 'The rated message could not be located in this conversation.',
        sessionUnavailable: 'The session for this feedback is no longer available.',
      },
    },
    offers: {
      title: 'Offers',
      permissions: {
        offers_create: 'Create Offers',
        offers_read: 'View Offers',
        offers_update: 'Update Offers',
        offers_delete: 'Delete Offers',
      },
    },
    templates: {
      title: 'Templates',
      permissions: {
        templates_update: 'Update Templates',
      },
    },
    sourcing: {
      title: 'Sourcing',
      permissions: {
        sourcing_read: 'View Sourced Prospects',
        sourcing_search: 'Run Sourcing Searches',
        sourcing_update: 'Update Sourced Prospects',
      },
    },
    messaging_connections: {
      title: 'Messaging Connections',
      permissions: {
        messaging_connections_create: 'Connect Messaging Accounts',
        messaging_connections_read: 'View Messaging Connections',
        messaging_connections_update: 'Update Messaging Connections',
        messaging_connections_delete: 'Disconnect Messaging Accounts',
      },
    },
    prospect_outreach: {
      title: 'Prospect Outreach',
      permissions: {
        prospect_outreach_create: 'Send Outreach Messages',
        prospect_outreach_read: 'View Outreach History',
        prospect_outreach_update: 'Edit Scheduled Follow-ups',
        prospect_outreach_delete: 'Cancel Scheduled Follow-ups',
      },
    },
    module: {
      title: 'Module | Modules',
      name: 'Name',
      atlas_agent: 'Atlas Agent',
      audit_access: 'Audit Access',
      group_management: 'Group Management',
      interview_management: 'Interview Management',
      interview_settings_management: 'Interview Settings Management',
      organization_settings_management: 'Organization Settings Management',
      scorecard_access: 'Scorecard Access',
      user_management: 'User Management',
      noModules: 'No modules found.',
      jobs_management: 'Jobs Management',
      candidates_management: 'Candidates Management',
      offers_management: 'Offers Management',
      messages_management: 'Messages Management',
      templates_management: 'Templates Management',
      candidate_sourcing_management: 'Candidate Sourcing Management',
      messaging_connections_management: 'Messaging Connections Management',
      prospect_outreach_management: 'Prospect Outreach Management',
    },
    operation: {
      title: 'Operation | Operations',
      noOperations: 'No operations found.',
    },
    groups: {
      title: 'Group | Groups',
      name: 'Name',
      manage: 'Manage Groups',
      selectOrganization: 'Select an Organization to view its Groups',
      viewTitle: 'Groups for',
      new: 'New Group',
      editGroup: 'Edit Group',
      deleteGroup: 'Delete Group',
      deleteGroupWarning:
        'This operation is irreversible. It will delete the Group and all permissions associated with users in this group.',
      deleteGroupDescription: 'Enter the name of the group to delete ({deleteGroupName}):',
      nameHint: 'Enter group name',
      noGroups: 'No groups found.',
      permissions: {
        groups_create: 'Create Permission Groups',
        groups_read: 'View Permission Groups',
        groups_update: 'Update Permission Groups',
        groups_delete: 'Delete Permission Groups',
      },
      toast: {
        permissionsUpdated: 'Permissions updated',
        groupNameRequired: 'Group name is required',
        groupCreated: 'Group created',
        groupUpdated: 'Group updated',
        groupDeleted: 'Group deleted',
      },
    },
    jobs: {
      title: 'Job | Jobs',
      jobTitle: 'Title',
      candidates: 'Candidates',
      noJobs: 'No jobs found.',
      permissions: {
        jobs_create: 'Create Jobs',
        jobs_read: 'View Jobs',
        jobs_update: 'Update Jobs',
        jobs_delete: 'Delete Jobs',
      },
    },
    careersSite: {
      title: 'Careers Site',
      syncJobs: 'Sync Jobs',
      syncSettings: 'Sync Settings',
      syncAll: 'Sync All',
      syncJobsDescription: 'Sync all published jobs to the careers site',
      syncSettingsDescription: 'Sync organization settings to the careers site',
      syncAllDescription: 'Sync both jobs and settings to the careers site',
      syncing: 'Syncing...',
      syncSuccess: 'Sync initiated successfully',
      syncError: 'Failed to sync',
    },
    subscriptions: {
      tabs: {
        details: 'Details',
        subscription: 'Subscription',
        aiCredits: 'AI Credits',
      },
      currentPlan: 'Current Plan',
      subscriptionDetails: "Your organization's subscription details",
      changePlan: 'Change Plan',
      selectNewPlan: 'Select a new plan for this organization',
      newPlan: 'New Plan',
      planDetails: 'Plan Details',
      users: 'Users',
      jobDescriptions: 'Job Descriptions',
      aiCreditsMonthly: 'AI Credits / Month',
      enabledAgents: 'Enabled AI Agents',
      noAgents: 'No agents enabled',
      unlimited: 'Unlimited',
      downgradeWarning: 'Downgrade Warning',
      current: 'current',
      allowed: 'allowed',
      confirmDowngrade: 'Are you sure you want to proceed?',
      confirmChange: 'Confirm Change',
      planChanged: 'Plan changed successfully',
      enterValue: 'Enter value',
      overridesCleared: 'Custom limits cleared successfully',
      planLimits: {
        sectionTitle: 'Plan Limits',
        editTitle: 'Edit plan limits',
        editDescription:
          "Customize this organization's plan limits and enabled agents. Changes apply only to this organization.",
        reason: 'Reason',
        reasonPlaceholder: 'e.g., Customer signed enterprise contract with custom terms',
        reasonHint: 'This will be recorded for audit purposes',
        customized: 'Customized',
        vsCatalog: 'vs plan: {value}',
        onVersionN: 'On v{current}; latest is v{latest}',
        footgunWarning:
          'These customizations will be reset if a new plan version is later pushed to existing customers. The change is recorded in subscription history.',
        saved: 'Plan limits saved successfully',
        resetToCatalog: 'Reset to plan defaults',
        resetConfirmTitle: 'Reset to plan defaults',
        resetConfirmBody:
          'This will discard all custom limits and reset this organization to the current plan defaults.',
        atLeastOneFieldRequired: 'Change at least one field to save.',
      },
      history: {
        title: 'Subscription History',
        description: 'Timeline of subscription changes',
        noHistory: 'No subscription history available',
        date: 'Date',
        event: 'Event',
        details: 'Details',
        notes: 'Notes',
        changedByColumn: 'Changed By',
        eventType: {
          CREATED: 'Subscription Created',
          PLAN_CHANGED: 'Plan Changed',
          CANCELLED: 'Subscription Cancelled',
          OVERRIDE_CHANGED: 'Custom limits edited',
          CATALOG_VERSION_APPLIED: 'Plan version pushed',
        },
        to: 'to',
        system: 'System',
      },
    },
    plans: {
      title: 'Plan | Plans',
      description: 'Manage the plans Atlast sells.',
      list: {
        includeArchived: 'Include archived plans',
        empty: 'No plans found.',
        activeBadge: 'Active',
        archivedBadge: 'Archived',
      },
      card: {
        perMonth: 'month',
        annualLine: '{price} billed annually',
        annualSavings: 'save {percent}%',
        usersLine: '{count} users',
        creditsLine: '{count} AI credits / month',
        jobsLine: '{count} job descriptions',
      },
      actions: {
        create: 'Create plan',
        edit: 'Edit',
        saveChanges: 'Save changes',
        archive: 'Archive plan',
        pushToCustomers: 'Push to existing customers',
        resetToCatalog: 'Reset to plan defaults',
        applyThisVersion: 'Apply this version',
      },
      fields: {
        code: 'Code',
        name: 'Name',
        description: 'Description',
        monthlyPrice: 'Monthly price',
        annualPrice: 'Annual price',
        maxUsers: 'Max users',
        aiCreditsMonthly: 'AI credits / month',
        maxJobDescriptions: 'Max job descriptions',
        enabledAgents: 'Enabled AI agents',
        version: 'Version',
        isActive: 'Active',
        archivedAt: 'Archived at',
        contactSales: 'Contact sales',
        currency: 'Currency',
        publicApiJobsEnabled: 'Public Jobs API',
        publicApiJobsEnabledHint:
          'Allow this plan to access the customer-facing Jobs API and webhooks',
        enabledModules: 'Enabled modules',
        enabledModulesDescription:
          'Modules a new self-serve organization is provisioned with when this plan is purchased.',
        enabledModulesEmptyWarning:
          'A priced plan with no modules selected will not be purchasable.',
        moduleNotAvailable: 'Not yet available',
      },
      tabs: {
        overview: 'Overview',
        versionHistory: 'Version history',
        subscribers: 'Subscribers',
      },
      apply: {
        previewTitle: 'Push to existing customers',
        explanation:
          'Applies version {version} of "{name}" to every subscriber currently on this plan. Each affected organization gets a fresh snapshot of this version — their price, limits, and enabled agents are replaced with the values shown below.',
        dryRunHint:
          'You will run a dry-run next to confirm exactly how many subscribers will be affected before any change is written.',
        confirmHint:
          'Click "Apply" to apply version {version} to {count} subscriber(s). This writes new per-organization plan snapshots and records a "Plan version pushed" entry in each subscription history.',
        previewBody:
          '{matched} organization(s) on this plan. {willMove} will be moved to v{version}.',
        dryRunButton: 'Run dry-run',
        confirmButton: 'Apply to {count} subscriber(s)',
        matchedSubscriptions: 'Matched subscriptions',
        willBeMoved: 'Will be moved',
        alreadyUpToDate: 'All subscribers are already on this version.',
        noSubscribers: 'No subscribers on this plan.',
        success: '{count} subscriber(s) moved to v{version}',
        archivedConflict:
          'This plan version is no longer available. Refresh the page and try again.',
        footgun:
          'This will replace any custom limits set via Edit plan limits on these subscribers.',
      },
      archive: {
        title: 'Archive plan',
        body: 'Archived plans cannot be assigned to new organizations. Existing subscribers keep their current plan snapshot.',
        confirmation: 'Type {code} to confirm',
        button: 'Archive plan',
      },
      sync: {
        menuLabel: 'Sync',
        fullSync: 'Full sync to Stripe',
        republishOnly: 'Republish plans.json only',
        confirm: {
          title: 'Full sync to Stripe?',
          body: 'This pushes every active priced plan to Stripe (products and prices) and then republishes plans.json to the CDN. Stripe changes are idempotent. Continue?',
          button: 'Sync to Stripe',
        },
        success: 'Synced {synced} plan(s), skipped {skipped}. plans.json republished.',
        republishSuccess: 'plans.json republished to the CDN.',
        result: {
          title: 'Sync completed with issues',
          summary: 'Synced {synced} plan(s), skipped {skipped}.',
          planErrorsTitle: 'Plans that failed to sync to Stripe',
          planErrorRow: '{code} v{version}: {message}',
          publishFailed: 'plans.json republish failed: {error}',
          publishOk: 'plans.json republished successfully.',
          close: 'Close',
        },
      },
      error: {
        codeAlreadyExists: 'A plan with this code already exists.',
        unknownCode: 'Plan not found.',
        atLeastOneFieldRequired: 'Change at least one field to save.',
        invalidSlug:
          'Code must start with a lowercase letter and contain only lowercase letters, digits, underscores or hyphens (2-41 chars).',
        concurrentEdit: 'Another admin just edited this plan. Refresh and try again.',
        codeRequired: 'Code is required.',
        nameRequired: 'Name is required.',
        invalidPrice: 'Price must be a non-negative integer or empty.',
        invalidLimit: 'Limit must be -1 (unlimited) or a non-negative integer.',
        invalidAgent: 'One or more selected agents are not recognized.',
      },
    },
    organization_settings: {
      title: 'Company Settings',
      permissions: {
        organization_settings_create: 'Create Company Settings',
        organization_settings_read: 'View Company Settings',
        organization_settings_update: 'Update Company Settings',
        organization_settings_delete: 'Delete Company Settings',
      },
    },
    audit: {
      title: 'Audit Logs',
      permissions: {
        audit_read: 'View Audit Logs',
      },
    },
    interview: {
      title: 'Interviews',
      permissions: {
        interview_create: 'Create Interviews',
        interview_delete: 'Delete Interviews',
        interview_read: 'View Interviews',
        interview_update: 'Update Interviews',
      },
    },
    interview_settings: {
      title: 'Interview Settings',
      permissions: {
        interview_settings_create: 'Create Interview Settings',
        interview_settings_delete: 'Delete Interview Settings',
        interview_settings_read: 'View Interview Settings',
        interview_settings_update: 'Update Interview Settings',
      },
    },
    scorecard: {
      title: 'Scorecards',
      permissions: {
        scorecard_create: 'Create Scorecards',
        scorecard_delete: 'Delete Scorecards',
        scorecard_read: 'View Scorecards',
        scorecard_update: 'Update Scorecards',
      },
    },
    help: {
      title: 'Help',
      userStatus: 'User Status',
      userStatusDescription: 'The user status can be one of the following',
    },
    dev: {
      title: 'Dev Corner',
    },
    technical: {
      title: 'Technical',
    },
    maintenance: {
      title: 'System Maintenance',
      workflowSnapshots: {
        title: 'Workflow Snapshots Cleanup',
        description:
          'Manually trigger cleanup of expired Mastra workflow snapshots (older than 24 hours). This runs automatically at midnight.',
        button: 'Cleanup Snapshots',
        cleaning: 'Cleaning...',
        success: 'Cleaned up {count} expired workflow snapshots',
      },
      reconcileConnections: {
        title: 'Reconcile LinkedIn Connections',
        description:
          'Check all LinkedIn (UniPile) connections against the provider and mark any whose account no longer exists as disconnected. This runs automatically daily and on send; use this to reconcile now.',
        button: 'Reconcile Now',
        reconciling: 'Reconciling...',
        success: 'Checked {checked} connections, disconnected {disconnected}',
      },
    },
    admin_users: {
      title: 'Admin User | Admin Users',
      listing: 'Admin Users List',
      editPermissions: 'Edit Permissions',
      name: 'Name',
      email: 'Email',
      operationsCount: 'Permissions',
      noAdminUsers: 'No admin users found.',
      permissions: {
        title: 'Permissions',
        description: 'Select the permissions for this admin user',
        module: 'Module',
        actions: 'Actions',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        noChanges: 'No changes to save',
        saveSuccess: 'Permissions saved successfully',
        saveError: 'Failed to save permissions',
        save: 'Save Permissions',
        saving: 'Saving...',
      },
      modules: {
        orgs: 'Organizations',
        users: 'Users',
        subs: 'Subscriptions',
        plan_catalog: 'Plans',
        features: 'Features',
        org_access: 'Organization Access',
        permission_template: 'Permission Templates',
        jobs: 'Jobs',
        careers: 'Careers',
        ai: 'AI',
        redis: 'Redis',
        admin_users: 'Admin Users',
        feedback: 'Feedback',
        system: 'System',
      },
      actions: {
        create: 'Create',
        read: 'Read',
        update: 'Update',
        delete: 'Delete',
        impersonate: 'Impersonate',
      },
      deleteAdminUser: {
        title: 'Delete Admin User',
        warning:
          'This will permanently delete the admin user and revoke their active session. This action cannot be undone.',
        confirmationInput: 'Type the email "{email}" to confirm deletion:',
        success: 'Admin user deleted successfully',
        errors: {
          notFound: 'Admin user not found. The list has been refreshed.',
          forbidden: 'You do not have permission to delete admin users.',
          cannotDeleteSelf: 'You cannot delete your own account.',
          generic: 'Failed to delete admin user',
        },
      },
    },
    permission_templates: {
      title: 'Permission Template | Permission Templates',
      listing: 'Permission Templates',
      showInactive: 'Show inactive',
      active: 'Active',
      inactive: 'Inactive',
      noTemplates: 'No permission templates found.',
      adminFreeze: {
        title: 'Adding a new system operation?',
        body: 'The Admin template is a frozen snapshot of operations — it does not auto-grow. When you add a new operation to the platform, add it to the Admin template via "Edit operations", then click "Notify" so linked orgs pick it up.',
      },
      actions: {
        create: 'New Template',
        compare: 'Compare',
        backToList: 'Back to list',
        editDetails: 'Edit details',
        saveDetails: 'Save details',
        editOperations: 'Edit operations',
        saveOperations: 'Save operations',
      },
      compare: {
        title: 'Compare Templates',
        subtitle: 'See at a glance which operations each template grants.',
        operationsColumn: 'Operations',
        searchPlaceholder: 'Search operations…',
        noTemplates: 'No templates to compare.',
        noOperations: 'No operations match your search.',
        granted: 'Granted',
        notGranted: 'Not granted',
      },
      sections: {
        details: 'Details',
        detailsHint: 'Name, description, default flag and ordering. Editing these does NOT change the version.',
        operations: 'Operations',
        operationsCreateHint: 'Select the operations this template grants. The template starts at version 1.',
        operationsEditHint: 'Replacing the operation set bumps the version if the set actually changed.',
      },
      fields: {
        name: 'Name',
        description: 'Description',
        version: 'Version',
        isDefault: 'Default',
        isDefaultHint: 'Default templates are automatically provisioned to NEW organisations when they sign up.',
        isDefaultWarningTitle: 'Applies to new organisations only',
        isDefaultWarning: 'Marking this template as default provisions it to organisations created from now on. Existing organisations are not retroactively granted it.',
        sortOrder: 'Sort order',
        sortOrderHint: 'Lower numbers are provisioned first.',
        operationCount: 'Operations',
        linkedGroupCount: 'Linked groups',
        status: 'Status',
      },
      operations: {
        searchPlaceholder: 'Search operations…',
        selectedCount: '{count} selected',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        noOperations: 'No operations to display.',
        versionWarningTitle: 'This change can bump the version',
        versionWarning: 'Saving a different operation set increments the template version. Linked organisations will then see an available update (notify them separately).',
        versionBumped: 'Operations saved — template is now version {version}.',
        noVersionChange: 'Operations saved — the set was unchanged, so the version stayed the same.',
      },
      notify: {
        title: 'Notify linked organisations',
        description: 'Send an in-app notification to organisations whose linked groups are behind the current version. This is a deliberate, manual step — editing a template never notifies automatically.',
        action: 'Notify linked orgs',
        inactiveHint: 'Inactive templates cannot send notifications. Re-enable the template first.',
        inactiveError: 'Cannot notify: this template is inactive.',
        resultTitle: 'Last notification result',
        resultCounts: 'Notified {orgs} organisation(s), {users} user(s).',
        idempotencyNote: 'Re-clicking is safe — orgs already notified for this version receive nothing.',
      },
      disable: {
        action: 'Disable',
        title: 'Disable template',
        calloutTitle: 'Linked groups keep working',
        calloutBody: 'Disabling hides this template from new applies and creates. The {count} group(s) already linked to it keep functioning and keep their link.',
        description: 'Are you sure you want to disable "{name}"?',
        confirm: 'Disable',
        success: 'Template disabled.',
      },
      enable: {
        action: 'Enable',
        title: 'Enable template',
        description: 'Re-enable "{name}" so it can be applied and used to create new groups again?',
        confirm: 'Enable',
        success: 'Template enabled.',
      },
      error: {
        nameRequired: 'Name is required.',
        sortOrderInvalid: 'Sort order must be a non-negative whole number.',
        nameExists: 'A template with this name already exists.',
        operationsRequired: 'Select at least one operation.',
        invalidOperations: 'One or more selected operations are invalid.',
      },
    },
    redis: {
      title: 'Redis & Queues',
      refresh: 'Refresh',
      connectionStatus: 'Connection Status',
      connected: 'Connected',
      disconnected: 'Disconnected',
      aggregatedStats: 'Aggregated Statistics',
      queues: 'Queues',
      paused: 'Paused',
      failedJobs: 'Failed Jobs',
      noFailedJobs: 'No failed jobs in this queue.',
      totalFailedJobs: '{count} failed job(s)',
      retryAll: 'Retry All',
      retry: 'Retry',
      deleteAll: 'Delete All',
      lastUpdated: 'Last updated',
      jobId: 'Job ID',
      jobName: 'Job Name',
      failedReason: 'Failed Reason',
      attempts: 'Attempts',
      viewDetails: 'View Details',
      jobDetails: 'Job Details',
      jobDetailsDescription: 'Complete information about this job',
      state: 'State',
      createdAt: 'Created At',
      processedOn: 'Processed On',
      finishedOn: 'Finished On',
      stacktrace: 'Stack Trace',
      jobData: 'Job Data',
      deleteJob: 'Delete Job',
      confirmDeleteTitle: 'Delete Failed Job',
      confirmDeleteDescription:
        'Are you sure you want to delete job {jobId}? This action cannot be undone.',
      confirmDeleteAllTitle: 'Delete All Failed Jobs',
      confirmDeleteAllDescription:
        'Are you sure you want to delete all {count} failed job(s) from the {queue} queue? This action cannot be undone.',
      stats: {
        waiting: 'Waiting',
        active: 'Active',
        completed: 'Completed',
        failed: 'Failed',
        delayed: 'Delayed',
      },
      health: {
        healthy: 'Healthy',
        warning: 'Warning',
        critical: 'Critical',
      },
      // Tabs
      tabs: {
        queues: 'Queues',
        keys: 'Keys',
        repeatableJobs: 'Repeatable Jobs',
      },
      // Pause/Resume
      pause: 'Pause',
      resume: 'Resume',
      confirmPauseTitle: 'Pause Queue Processing',
      confirmPauseDescription:
        'Are you sure you want to pause the {queue} queue? Active jobs will complete, but no new jobs will be processed until resumed.',
      queuePaused: 'Queue {queue} has been paused',
      queueResumed: 'Queue {queue} has been resumed',
      // Repeatable Jobs
      repeatableJobs: 'Repeatable Jobs',
      noRepeatableJobs: 'No repeatable jobs found.',
      repeatableJobName: 'Job Name',
      repeatableJobPattern: 'Pattern',
      repeatableJobNextRun: 'Next Run',
      confirmRemoveRepeatableTitle: 'Remove Repeatable Job',
      confirmRemoveRepeatableDescription:
        'Are you sure you want to remove the repeatable job "{jobName}" from the {queue} queue? This will stop it from running automatically.',
      repeatableJobRemoved: 'Repeatable job removed successfully',
      allQueues: 'All Queues',
      filterByQueue: 'Filter by queue',
      // Key Browser
      keyBrowser: 'Key Browser',
      searchPattern: 'Pattern',
      searchPatternPlaceholder: 'e.g., cv-eval-*, candidate-otp-*',
      search: 'Search',
      noKeysFound: 'No keys found matching the pattern.',
      keyName: 'Key',
      keyType: 'Type',
      keyTtl: 'TTL',
      keyValue: 'Value',
      keyEncoding: 'Encoding',
      loadMore: 'Load More',
      viewKey: 'View Key Details',
      confirmDeleteKeyTitle: 'Delete Redis Key',
      confirmDeleteKeyDescription:
        'Are you sure you want to delete the key "{key}"? This action cannot be undone.',
      keyDeleted: 'Key deleted successfully',
      ttlNoExpiry: 'No expiry',
      enterSearchPattern: 'Enter a pattern to search for Redis keys',
      keyDetails: 'Key Details',
      keyDetailsDescription: 'View the contents and metadata of this Redis key',
    },
    messages: {
      title: 'Messages',
      permissions: {
        messages_create: 'Create Messages',
        messages_read: 'View Messages',
        messages_update: 'Update Messages',
        messages_delete: 'Delete Messages',
      },
    },
  },
  common: {
    language: 'Language',
    dark: 'Dark',
    light: 'Light',
    auto: 'Auto',
    loading: 'Loading...',
    tryAgain: 'Try Again',
    yes: 'Yes',
    no: 'No',
    total: 'Total',
    enabled: 'Enabled',
    disabled: 'Disabled',
    showId: 'Show {id}',
    copy: 'Copy',
    save: 'Save',
    cancel: 'Cancel',
    messages: {
      loading: 'Loading...',
      noResults: 'No results found.',
      createdSuccessfully: '{item} created successfully!',
      updatedSuccessfully: '{item} updated successfully!',
    },
    actions: {
      title: 'Actions',
      warning: 'Warning',
      create: 'Create',
      cancel: 'Cancel',
      update: 'Update',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      deleting: 'Deleting...',
      back: 'Back',
    },
  },
  organizations: {
    errors: {
      invalid_subdomain_format:
        'Subdomain must be lowercase alphanumeric characters and hyphens only, between 3 and 63 characters',
      reserved_subdomain:
        'This subdomain is reserved and cannot be used. Please choose a different subdomain.',
      modules_required: 'At least one module must be assigned when creating an organization',
      subdomain_already_exists: "An organization with subdomain '{subdomain}' already exists",
      name_already_exists: "An organization with name '{name}' already exists",
      conflict: 'Organization could not be created due to a conflict. Please try again.',
    },
  },
  error: {
    error: 'Error',
    failedToFetch: 'Failed to fetch data. Please try again later.',
    message: 'An error occurred. Please try again later.',
  },
  versionUpdate: {
    title: 'New version available',
    message: 'Refresh now to update, or we will update on your next navigation.',
    refreshNow: 'Refresh',
    updatedTitle: 'Updated to v{version}',
    updatedMessage: "You're now on the latest version.",
  },
}
